"use client";
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { ChevronDown, Store, Plus } from 'lucide-react';
import { useSession } from 'next-auth/react';

export default function GlobalStoreSelector() {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [showSelector, setShowSelector] = useState(false);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const { data: session, status } = useSession();

  useEffect(() => {
    if (status === 'authenticated') {
      loadStoresAndPreference();
    }
  }, [status]);

  const loadStoresAndPreference = async () => {
    try {
      const pathname = window.location.pathname;
      
      // Skip on Installation Wizard or auth pages
      if (pathname.includes('/InstallationWizard') || pathname.includes('/auth/')) {
        setLoading(false);
        return;
      }

      const res = await fetch('/api/user-stores');
      const data = await res.json();

      // If API error or no session, don't show anything
      if (!data.success && data.error) {
        console.error('Store fetch error:', data.error);
        setLoading(false);
        return;
      }

      if (data.success) {
        const userStores = data.stores || [];
        setStores(userStores);

        // IMPORTANT: If no stores, redirect to wizard - don't show selector
        if (userStores.length === 0) {
          console.log('No stores found, redirecting to Installation Wizard');
          router.push('/InstallationWizard');
          return;
        }

        // If has stores and preference, auto-select
        if (data.lastSelected && userStores.some(s => s.shop === data.lastSelected)) {
          setSelectedStore(data.lastSelected);
          setShowSelector(false);
        } else if (userStores.length === 1) {
          // Only 1 store - auto-select it
          const autoSelectedShop = userStores[0].shop;
          setSelectedStore(autoSelectedShop);
          setShowSelector(false);
          // Save preference
          await handleStoreSelect(autoSelectedShop);
        } else {
          // Multiple stores, no preference - show selector
          setShowSelector(true);
        }
      }
    } catch (error) {
      console.error('Error loading stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStoreSelect = async (shop) => {
    try {
      const res = await fetch('/api/user-stores/select', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop })
      });

      if (res.ok) {
        setSelectedStore(shop);
        setShowSelector(false);

        // Set cookie for immediate use
        document.cookie = `selectedStore=${shop}; path=/`;

        // Reload page with new store context
        window.location.reload();
      }
    } catch (error) {
      console.error('Error selecting store:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // Don't show on auth pages or Installation Wizard
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const isAuthPage = pathname.includes('/auth/');
  const isInstallationWizard = pathname.includes('/InstallationWizard');
  
  if (status === 'unauthenticated' || loading || isAuthPage || isInstallationWizard) {
    return null;
  }

  // Force selection modal (only when needed and not on wizard)
  if (showSelector || (!selectedStore && stores.length > 0)) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden relative">
          {/* Close Button */}
          <button
            onClick={() => {
              setShowSelector(false);
              // Redirect to Installation Wizard
              router.push('/InstallationWizard');
            }}
            className="absolute top-2 right-2 z-10 p-2 rounded-full bg-red-500/80 hover:bg-red-600 text-white transition-colors shadow-lg"
            title="Close and go to Installation Wizard"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2.5}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          
          {/* Header */}
          <div className="bg-gradient-to-r from-[#284F9E] to-[#1a3a7a] p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <Store className="w-6 h-6" />
              <h2 className="text-2xl font-bold">Select a Store</h2>
            </div>
            <p className="text-blue-100 text-sm">
              Choose which Shopify store you want to work with
            </p>
          </div>

          {/* Store List */}
          <div className="p-6 max-h-96 overflow-y-auto">
            <div className="space-y-3">
              {stores.map((store) => (
                <button
                  key={store.id}
                  onClick={() => handleStoreSelect(store.shop)}
                  className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-[#284F9E] hover:bg-blue-50 transition-all text-left group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#284F9E] flex items-center justify-center text-white font-bold text-lg shrink-0">
                      {store.shop.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 truncate group-hover:text-[#284F9E]">
                        {store.shop}
                      </div>
                      <div className="text-sm text-gray-500">
                        Last used: {formatDate(store.last_used)}
                      </div>
                    </div>
                    <ChevronDown className="w-5 h-5 text-gray-400 -rotate-90 group-hover:text-[#284F9E]" />
                  </div>
                </button>
              ))}
            </div>

            {/* Connect New Store */}
            <button
              onClick={() => router.push('/InstallationWizard')}
              className="w-full mt-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#284F9E] hover:text-[#284F9E] hover:bg-blue-50 transition-all flex items-center justify-center gap-2 font-medium"
            >
              <Plus className="w-5 h-5" />
              Connect New Store
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Compact store indicator in navbar (when store is selected)
  if (selectedStore && stores.length > 0) {
    return (
      <div className="hidden md:flex items-center gap-2">
        <span className="text-xs text-gray-500 font-medium">Store:</span>
        <button
          onClick={() => setShowSelector(true)}
          className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg hover:from-blue-100 hover:to-indigo-100 transition-all border border-blue-200 group"
        >
          <div className="w-6 h-6 rounded-full bg-[#284F9E] flex items-center justify-center text-white text-xs font-bold">
            {selectedStore.charAt(0).toUpperCase()}
          </div>
          <span className="font-medium text-gray-700 text-sm max-w-[150px] truncate">
            {selectedStore.replace('.myshopify.com', '')}
          </span>
          <ChevronDown className="w-4 h-4 text-gray-500 group-hover:text-[#284F9E] transition-colors" />
        </button>
      </div>
    );
  }

  return null;
}

